<template>
  <Card class="container flex flex-col justify-between">
    <h1 class="lg:text-2xl font-bold mb-4 truncate">
      {{
        translate("site.org.id.followers.users_who_follow_this_org", {
          name: displayName,
        })
      }}
    </h1>
    <div
      class="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      <UserProfileCard
        class="flex-grow"
        v-for="user in usersFollowing"
        :key="user.id"
        :user="user"
        v-model:following="userFollowsMap[user.id]"
        :follows-you="
          authStore?.user?.profile?.sub !== user.id
            ? followingYouMap[user.id]
            : undefined
        "
        :disable-follow="followCheckPending"
      />
    </div>
    <NamiLoading v-if="pending" />
    <div v-else-if="error">
      {{ error }}
    </div>
    <NoResults
      v-else-if="usersFollowing.length === 0"
      :message="translate('site.org.id.followers.no_followers')"
    />
  </Card>
</template>

<script setup lang="ts">
import {
  User,
  type UserEntity,
  getMultipleRelationships,
  isUser,
  type OrganizationEntity,
  Organization,
} from "~/src/api";
import { parseOrgForDisplay } from "~/utils/display/org";

const props = defineProps<{
  org: OrganizationEntity;
}>();

const route = useRoute();
const nuxtApp = useNuxtApp();
const translate = nuxtApp.$i18n.global.t;
const authStore = nuxtApp.$auth();

const limit = ref(parseInt((route.query.limit as string) ?? 32));
const userFollowsMap = reactive<Record<string, boolean>>({});
const followingYouMap = reactive<Record<string, boolean>>({});
const usersFollowing = ref<UserEntity[]>([]);
const followingMeta = ref({
  offset: 0,
  limit: limit.value,
  total: 0,
});

const { displayName } = parseOrgForDisplay(computed(() => props.org));

const {
  pending,
  error,
  refresh: loadMore,
} = useAsyncData(
  `users-following-org-${props.org.id}`,
  async () => {
    const token = (await authStore?.getToken()) ?? undefined;
    const followingUsers = await Organization.getFollows(
      {
        organizationId: props.org.id,
        includes: ["user"],
        offset: usersFollowing.value.length,
        limit: limit.value,
      },
      token,
    );

    const usersChecked = followingUsers.data
      .map(
        (follow) => follow.relationships.find((rel) => rel.type === "user")?.id,
      )
      .filter(onlyTruthys);

    usersChecked.forEach((id) => {
      followingYouMap[id] = false;
      userFollowsMap[id] = false;
    });

    const userIds = followingUsers.data
      .map((follow) => getMultipleRelationships(follow, "user", true))
      .map((users) => users.find((rel) => rel.type === "user"))
      .filter(onlyTruthys)
      .filter(isUser)
      .map((user) => user.id);

    const res = await getUsersFromIds(userIds, ["profile_frame"], token);

    usersFollowing.value = usersFollowing.value.concat(res.data);
    followingMeta.value = followingUsers.meta;
  },
  { server: false },
);

const { pending: followCheckPending } = useAsyncData(
  async () => {
    if (!authStore?.user) return;
    const oldIds = Object.keys(usersFollowing.value);
    const newIds = usersFollowing.value
      .filter((user) => !oldIds.includes(user.id))
      .map((user) => user.id);
    if (!newIds.length) return;
    const usersChecked = newIds;

    if (authStore.user?.profile.sub) {
      await Promise.all([
        User.getFollows({
          targetId: authStore.user.profile.sub,
          originIds: usersChecked,
        }).then((resp) => {
          const foundUsers = resp.data
            .map(
              (follow) =>
                follow.relationships.find(
                  (rel) => rel.id !== authStore.user?.profile.sub,
                )?.id,
            )
            .filter(onlyTruthys);
          foundUsers.forEach((id) => (followingYouMap[id] = true));
        }),
        User.getFollows({
          targetIds: usersChecked,
          originId: authStore.user.profile.sub,
        }).then((resp) => {
          const foundUsers = resp.data
            .map(
              (follow) =>
                follow.relationships.find(
                  (rel) => rel.id !== authStore.user?.profile.sub,
                )?.id,
            )
            .filter(onlyTruthys);
          foundUsers.forEach((id) => (userFollowsMap[id] = true));
        }),
      ]);
    }
  },
  {
    server: false,
    watch: [() => authStore?.user, () => usersFollowing.value.length],
  },
);

const loadMoreIfNearBottom = () => {
  if (usersFollowing.value.length >= followingMeta.value.total || pending.value)
    return;
  if (
    window.scrollY + window.innerHeight >
    document.documentElement.scrollHeight - 300
  )
    loadMore();
};

onMounted(() => {
  window.addEventListener("scroll", loadMoreIfNearBottom);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", loadMoreIfNearBottom);
});
</script>
